import React from 'react';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';
import { Link } from 'core/routing';

import { clientStore } from 'apps/client/app';

import { Spacer } from 'styles/utils';

import { conf } from './config.yaml';

import {
  ContentIntroStyled,
  TagIntro,
  HeadingStyled,
  ParagraphStyled,
  ButtonStyled,
  GridWrapper,
  Container,
  GridCellStyled,
  HeroButtons,
  Pattern,
  PatternPath,
  BackgroundLayerStyled,
  Center,
  RegualBlock,
} from './contentIntro.styled';

function GridCell({ polygon, polygonColor, children }) {
  return (
    <>
      <GridCellStyled
        polygon={polygon}
        polygonColor={polygonColor}
        aria-hidden={!children}
      >
        {children}
      </GridCellStyled>
    </>
  );
}

GridCell.propTypes = {
  polygon: PropTypes.string,
  polygonColor: PropTypes.string,
  children: PropTypes.node,
};

const ContentIntro = withNamespaces()(
  ({
    title,
    tag,
    description,
    createButtonText,
    createButtonLink,
    learnButtonText,
    learnButtonLink,
    hideButtonWhenSignIn = false,
    marginTop,
    marginBottom,
    isGrid,
    variant,
    hasPattern,
    isCentered,
    textCenter,
    addSpacer = false,
    backgroundLayer,
    hideIfSignedIn,
  }) => {
    if (hideIfSignedIn && clientStore.signedInProfile) {
      return null;
    }

    const smallDevice = ['xs'].includes(clientStore.activeBreakPoint);

    const GridBlock = () => (
      <GridWrapper backgroundLayer={backgroundLayer}>
        {tag && (
          <>
            <GridCell />
            <GridCell />
            <GridCell>
              <TagIntro>{tag}</TagIntro>
            </GridCell>
          </>
        )}
        {title && (
          <>
            <GridCell polygon='triangle' polygonColor='wfYellow' />
            <GridCell polygon='circle' />
            <GridCell>
              <HeadingStyled size='xxlarge'>{title}</HeadingStyled>
            </GridCell>
          </>
        )}
        {description && (
          <>
            <GridCell />
            <GridCell />
            <GridCell>
              <ParagraphStyled size='medium'>
                <Trans>{description}</Trans>
              </ParagraphStyled>
            </GridCell>
          </>
        )}
      </GridWrapper>
    );

    return (
      <ContentIntroStyled
        variant={variant}
        marginTop={marginTop}
        marginBottom={marginBottom}
      >
        {backgroundLayer && (
          <BackgroundLayerStyled>{backgroundLayer}</BackgroundLayerStyled>
        )}
        <Container
          variant={variant}
          isGrid={isGrid}
          isCentered={isCentered}
          textCenter={textCenter}
        >
          {isGrid ? (
            <GridBlock />
          ) : (
            <>
              {backgroundLayer && <GridBlock />}
              <RegualBlock backgroundLayer={backgroundLayer}>
                {tag && <TagIntro>{tag}</TagIntro>}
                {addSpacer && tag && <Spacer size='default' />}
                {title && (
                  <HeadingStyled size='xxlarge' textCenter={textCenter}>
                    <Trans>{title}</Trans>
                  </HeadingStyled>
                )}
                {addSpacer && title && <Spacer size='xlarge' />}
                {description &&
                  (textCenter ? (
                    <Center>
                      <ParagraphStyled size='medium' textCenter={textCenter}>
                        <Trans>{description}</Trans>
                      </ParagraphStyled>
                    </Center>
                  ) : (
                    <ParagraphStyled size='medium' textCenter={textCenter}>
                      <Trans>{description}</Trans>
                    </ParagraphStyled>
                  ))}
              </RegualBlock>
            </>
          )}
          {createButtonText && (
            <HeroButtons isCentered={isCentered}>
              {createButtonText ? (
                hideButtonWhenSignIn && clientStore.signedInProfile ? null : (
                  <Link name={createButtonLink}>
                    <ButtonStyled
                      text={<Trans>{createButtonText}</Trans>}
                      appearance='hero'
                      fullWidth={smallDevice}
                      icon='arrow_right'
                    />
                  </Link>
                )
              ) : null}
              {learnButtonText && (
                <Link name={learnButtonLink}>
                  <ButtonStyled
                    text={<Trans>{learnButtonText}</Trans>}
                    fullWidth={smallDevice}
                    icon='arrow_right'
                    appearance='hero'
                    renderAsLink
                    outline
                  />
                </Link>
              )}
            </HeroButtons>
          )}
        </Container>
        {hasPattern && (
          <Pattern
            width='100%'
            height='100%'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden={true}
          >
            <defs>
              <pattern
                id='grid'
                width={conf.pattern.size}
                height={conf.pattern.size}
                patternUnits='userSpaceOnUse'
              >
                <PatternPath
                  d={`M ${conf.pattern.size} 0 L 0 0 0 ${conf.pattern.size}`}
                  fill='none'
                  variant={variant}
                />
              </pattern>
            </defs>
            <rect width='100%' height='100%' fill='url(#grid)' />
          </Pattern>
        )}
      </ContentIntroStyled>
    );
  },
);

export default ContentIntro;

ContentIntro.propTypes = {
  name: PropTypes.string,
  isCentered: PropTypes.bool,
  /**
   * This prop allows to change between three visual variants [primary, secondary & tertiary]
   */
  variant: PropTypes.string,
  hideIfSignedIn: PropTypes.bool,
};
